import type { DrugInteraction, Medicine } from '../types';
import { canonicalGenericName, isKnownGenericName } from './medicineValidator';
import { medcheckPrescriptionTrainingDatabase } from '../data/medcheckPrescriptionTrainingDatabase';

interface InteractionRule {
  genericA: string;
  genericB: string;
  severity: DrugInteraction['severity'];
  status: DrugInteraction['status'];
  description: string;
  recommendation: string;
}

const VERIFIED_INTERACTION_RULES: InteractionRule[] = [
  {
    genericA: 'Pregabalin',
    genericB: 'Clonazepam',
    severity: 'moderate',
    status: 'possible_issue',
    description: 'May increase sleepiness, dizziness, sedation, and fall risk.',
    recommendation: 'Use exactly as prescribed. Avoid alcohol or other sedatives unless the doctor advised them, and be careful with driving or walking if sleepy.',
  },
];

interface TrainingInteractionRule {
  pair?: readonly string[];
  severity?: string;
  patientMessage?: string;
}

const TRAINING_INTERACTION_RULES: InteractionRule[] = (
  (medcheckPrescriptionTrainingDatabase as unknown as { interactionRules?: readonly TrainingInteractionRule[] }).interactionRules || []
)
  .filter((rule) => Array.isArray(rule.pair) && rule.pair.length === 2)
  .map((rule) => ({
    genericA: rule.pair?.[0] || '',
    genericB: rule.pair?.[1] || '',
    severity: rule.severity === 'critical' || rule.severity === 'high'
      ? 'high'
      : rule.severity === 'low'
        ? 'low'
        : 'moderate',
    status: 'possible_issue',
    description: rule.patientMessage || 'This combination should be checked with a doctor or pharmacist.',
    recommendation: 'Use exactly as prescribed and confirm this combination with a doctor or pharmacist if symptoms occur.',
  }));

const LOCAL_INTERACTION_RULES = [...VERIFIED_INTERACTION_RULES, ...TRAINING_INTERACTION_RULES];

function splitGenericIngredients(generic?: string): string[] {
  if (!isKnownGenericName(generic)) return [];

  return canonicalGenericName(generic)
    .split(/\s*(?:\+|\/|,| and )\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);
}

function medicineHasGeneric(medicine: Medicine, generic: string): boolean {
  const target = canonicalGenericName(generic);
  return splitGenericIngredients(medicine.generic_name || medicine.genericName).includes(target);
}

function isLocallyVerified(medicine: Medicine): boolean {
  return Boolean(
    medicine.genericConfirmed &&
      medicine.identificationStatus === 'identified' &&
      (medicine.resolutionConfidence ?? medicine.confidence ?? 0) >= 0.84
  );
}

function getRuleForMedicines(a: Medicine, b: Medicine): InteractionRule | undefined {
  if (!isLocallyVerified(a) || !isLocallyVerified(b)) return undefined;

  return LOCAL_INTERACTION_RULES.find(
    (rule) =>
      (medicineHasGeneric(a, rule.genericA) && medicineHasGeneric(b, rule.genericB)) ||
      (medicineHasGeneric(a, rule.genericB) && medicineHasGeneric(b, rule.genericA))
  );
}

function interactionKey(a: string, b: string): string {
  return [a, b].map((value) => value.toLowerCase().trim()).sort().join('::');
}

export function getVerifiedLocalInteractions(medicines: Medicine[]): DrugInteraction[] {
  const interactions: DrugInteraction[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < medicines.length; i += 1) {
    for (let j = i + 1; j < medicines.length; j += 1) {
      const first = medicines[i];
      const second = medicines[j];
      const rule = getRuleForMedicines(first, second);
      if (!rule) continue;

      const key = interactionKey(first.name, second.name);
      if (seen.has(key)) continue;
      seen.add(key);

      interactions.push({
        severity: rule.severity,
        status: rule.status,
        drug1: first.name,
        drug2: second.name,
        description: rule.description,
        recommendation: rule.recommendation,
      });
    }
  }

  return interactions;
}

function findMedicineByName(value: string, medicines: Medicine[]): Medicine | undefined {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (!normalized) return undefined;

  return medicines.find((medicine) => {
    const candidates = [
      medicine.name,
      medicine.resolvedBrandName,
      medicine.generic_name,
      medicine.genericName,
    ]
      .filter(Boolean)
      .map((candidate) => String(candidate).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim());

    return candidates.some((candidate) => candidate === normalized || candidate.includes(normalized) || normalized.includes(candidate));
  });
}

function descriptionConflictsWithLocalGeneric(interaction: DrugInteraction, medicines: Medicine[]): boolean {
  const text = `${interaction.description} ${interaction.recommendation}`.toLowerCase();
  const first = findMedicineByName(interaction.drug1, medicines);
  const second = findMedicineByName(interaction.drug2, medicines);
  const pairs: Array<[Medicine | undefined, string]> = [
    [first, interaction.drug1],
    [second, interaction.drug2],
  ];

  return pairs.some(([medicine, label]) => {
    if (!medicine || !isLocallyVerified(medicine)) return false;
    const ingredients = splitGenericIngredients(medicine.generic_name || medicine.genericName);
    const labelText = label.toLowerCase();
    const mentionedKnownGeneric = [
      'empagliflozin',
      'linagliptin',
      'domperidone',
      'insulin',
      'pregabalin',
      'clonazepam',
    ].find((generic) => text.includes(generic) || labelText.includes(generic));

    return Boolean(mentionedKnownGeneric && !ingredients.includes(mentionedKnownGeneric));
  });
}

export function validateAiInteractions(
  interactions: DrugInteraction[],
  medicines: Medicine[]
): DrugInteraction[] {
  const localKeys = new Set(
    getVerifiedLocalInteractions(medicines).map((interaction) => interactionKey(interaction.drug1, interaction.drug2))
  );

  return interactions
    .filter((interaction) => {
      const first = findMedicineByName(interaction.drug1, medicines);
      const second = findMedicineByName(interaction.drug2, medicines);
      const hasRecognizedPair = Boolean(first && second);
      const conflicts = descriptionConflictsWithLocalGeneric(interaction, medicines);
      const key = interactionKey(first?.name || interaction.drug1, second?.name || interaction.drug2);
      const fullyLocallyVerifiedPair = Boolean(first && second && isLocallyVerified(first) && isLocallyVerified(second));
      const knownPairWithoutRule = fullyLocallyVerifiedPair && !localKeys.has(key);

      if ((!hasRecognizedPair || conflicts || knownPairWithoutRule) && import.meta.env.DEV) {
        console.debug('Discarded AI interaction due to unresolved or conflicting local medicine identity', interaction);
      }

      return hasRecognizedPair && !conflicts && !knownPairWithoutRule;
    })
    .map((interaction) => {
      const first = findMedicineByName(interaction.drug1, medicines);
      const second = findMedicineByName(interaction.drug2, medicines);
      const key = interactionKey(first?.name || interaction.drug1, second?.name || interaction.drug2);

      return {
        ...interaction,
        drug1: first?.name || interaction.drug1,
        drug2: second?.name || interaction.drug2,
        status: localKeys.has(key) ? interaction.status : 'possible_issue',
      };
    });
}

export function mergeInteractions(
  localInteractions: DrugInteraction[],
  aiInteractions: DrugInteraction[]
): DrugInteraction[] {
  const merged = new Map<string, DrugInteraction>();

  localInteractions.forEach((interaction) => {
    merged.set(interactionKey(interaction.drug1, interaction.drug2), interaction);
  });

  aiInteractions.forEach((interaction) => {
    const key = interactionKey(interaction.drug1, interaction.drug2);
    if (!merged.has(key)) {
      merged.set(key, { ...interaction, status: 'possible_issue' });
    }
  });

  return Array.from(merged.values());
}
